// React
import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Mantine
import { Grid, Loader, Modal, Text } from '@mantine/core';

// Interfaces
import { Claim, Debit } from 'interfaces';
import { CLAIM_STATUS } from '@billing/validation/baseClaim';
import { CLAIM_TARIFF_STATUS } from '@billing/validation/tariff';

// Components
import { ClaimQueryModalContent } from 'pages/claims/components';
import { MatchClaimModalAlert } from './MatchClaimModalAlert';
import { MatchedTariffCard } from './MatchedTariffCard';
import { UnpaidUnmatchedCard } from './UnpaidUnmatchedCard';

// API
import { useUpdateClaim } from 'api/resource';
import { useCanCreate } from 'hooks';

interface MatchClaimModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  title?: string;
  selectedClaim?: Claim;
}

type ClaimTariff = Claim['tariffs'][number];
type ReturnedDebit = Omit<Debit, 'tariff'> & { tariff?: ClaimTariff };

const getTariffIdentity = (tariff: ClaimTariff): string =>
  [
    tariff.province,
    tariff.prefix,
    tariff.code,
    tariff.specialtyCode?.trim() || 'ALL',
    tariff.effectiveDate,
  ].join(':');

export const MatchClaimModal: React.FC<MatchClaimModalProps> = React.memo(
  ({ open, setOpen, title = 'Match Claim', selectedClaim }) => {
    const updateClaim = useUpdateClaim();
    const { canCreate: canCreateQuery } = useCanCreate('queries');

    const [loading, setLoading] = useState<boolean>(false);
    const [showQuery, setShowQuery] = useState<boolean>(false);
    const [modifiedClaim, setModifiedClaim] = useState<Claim | null>(
      selectedClaim || null
    );

    const debits: Debit[] = Array.isArray(selectedClaim?.debits)
      ? selectedClaim.debits
      : [];
    const tariffs: ClaimTariff[] = Array.isArray(selectedClaim?.tariffs)
      ? selectedClaim.tariffs
      : [];

    const isReturnedAssessmentTariff = useCallback(
      (tariff?: ClaimTariff | null) =>
        tariff?.status?.trim() === CLAIM_TARIFF_STATUS.UNMATCHED,
      []
    );

    const getDebitTariff = useCallback(
      (debit: {
        tariffID: string;
        tariff?: Debit['tariff'] | ClaimTariff;
      }): ClaimTariff | undefined =>
        tariffs.find((tariff) => tariff.id === debit.tariffID) ??
        (debit.tariff as unknown as ClaimTariff | undefined),
      [tariffs]
    );

    // Matched tariffs: ones that have a matching debit and are not returned assessment rows
    const matchedTariffs = useMemo(
      () =>
        tariffs.filter(
          (tariff) =>
            debits.some((debit) => debit.tariffID === tariff.id) &&
            !isReturnedAssessmentTariff(tariff)
        ),
      [tariffs, debits, isReturnedAssessmentTariff]
    );

    // Returned assessment debits: debits not on the submitted claim, or claim tariff rows inserted as Unmatched.
    const unmatchedDebits: ReturnedDebit[] = useMemo(
      () =>
        debits
          .filter((debit) => {
            const claimTariff = tariffs.find(
              (tariff) => tariff.id === debit.tariffID
            );
            return !claimTariff || isReturnedAssessmentTariff(claimTariff);
          })
          .map((debit) => ({
            ...debit,
            tariff: getDebitTariff(debit),
          })),
      [debits, tariffs, getDebitTariff, isReturnedAssessmentTariff]
    );

    // Submitted tariffs that did not come back as paid/assessed on the remittance.
    const unpaidRequestedTariffs: ClaimTariff[] = useMemo(
      () =>
        tariffs.filter(
          (tariff) =>
            !debits.some((debit) => debit.tariffID === tariff.id) &&
            !isReturnedAssessmentTariff(tariff)
        ),
      [tariffs, debits, isReturnedAssessmentTariff]
    );

    const returnedTariffs: ClaimTariff[] = useMemo(
      () =>
        unmatchedDebits
          .map((debit) => getDebitTariff(debit))
          .filter(Boolean) as ClaimTariff[],
      [unmatchedDebits, getDebitTariff]
    );

    const [selectedTariffIDs, setSelectedTariffIDs] = useState<string[]>([]);

    const selectedTariffs: ClaimTariff[] = useMemo(
      () =>
        returnedTariffs.filter((tariff) =>
          selectedTariffIDs.includes(tariff.id)
        ),
      [returnedTariffs, selectedTariffIDs]
    );

    // Auto-select when there is only one option
    useEffect(() => {
      if (returnedTariffs.length === 1 && selectedTariffIDs.length === 0) {
        setSelectedTariffIDs([returnedTariffs[0].id]);
        return;
      }
      const returnedTariffIDs = new Set(
        returnedTariffs.map((tariff) => tariff.id)
      );
      const validSelectedTariffIDs = selectedTariffIDs.filter((id) =>
        returnedTariffIDs.has(id)
      );
      if (validSelectedTariffIDs.length !== selectedTariffIDs.length) {
        setSelectedTariffIDs(validSelectedTariffIDs);
      }
    }, [returnedTariffs, selectedTariffIDs]);

    const handleQueryTariffs = async (tariffsToQuery: ClaimTariff[]) => {
      if (!tariffsToQuery.length) return;
      const existingTariffIDs = new Set(tariffs.map((tariff) => tariff.id));
      const newTariffs = [
        ...tariffs,
        ...tariffsToQuery.filter((tariff) => !existingTariffIDs.has(tariff.id)),
      ];

      const newData = {
        ...(selectedClaim as Claim),
        tariffs: newTariffs,
      } as Claim;
      setModifiedClaim(newData);
      setShowQuery(true);
    };

    const handleAcceptExtraTariffs = async (tariffsToAccept: ClaimTariff[]) => {
      if (!tariffsToAccept.length) return;
      setLoading(true);
      try {
        // Accepted returned tariffs represent the payor's assessment, even
        // though these tariffs were not part of the original submission.
        const paidReturnedTariffs = tariffsToAccept.map((tariff) => ({
          ...tariff,
          units: tariff.units ?? 1,
          icd9: tariff.icd9 ?? (selectedClaim?.tariffs?.[0]?.icd9 || ''),
          status: CLAIM_TARIFF_STATUS.PAID,
        }));
        const paidReturnedTariffsByID = new Map(
          paidReturnedTariffs.map((tariff) => [tariff.id, tariff])
        );
        const unpaidRequestedTariffIDs = new Set(
          unpaidRequestedTariffs.map((requestedTariff) => requestedTariff.id)
        );
        const acceptedReturnedTariffIDs = new Set(
          paidReturnedTariffs.map((tariff) => tariff.id)
        );
        const acceptedReturnedDebitIDs = new Set(
          unmatchedDebits
            .filter((debit) => acceptedReturnedTariffIDs.has(debit.tariffID))
            .map((debit) => debit.id)
        );
        const acceptedTariffIdentities = new Set(
          paidReturnedTariffs.map(getTariffIdentity)
        );
        // Ingestion can leave a duplicate Unmatched claim-tariff row without
        // a debit. Remove only that orphan; submitted tariffs must remain so
        // their original billed amounts continue to feed financial metrics.
        const orphanedMatchingTariffIDs = new Set(
          tariffs
            .filter(
              (tariff) =>
                isReturnedAssessmentTariff(tariff) &&
                !debits.some((debit) => debit.tariffID === tariff.id) &&
                acceptedTariffIdentities.has(getTariffIdentity(tariff))
            )
            .map((tariff) => tariff.id)
        );
        const updatedExistingTariffs = tariffs
          .filter(
            (existingTariff) =>
              !orphanedMatchingTariffIDs.has(existingTariff.id)
          )
          .map((existingTariff) => {
            const paidReturnedTariff = paidReturnedTariffsByID.get(
              existingTariff.id
            );
            if (paidReturnedTariff) return paidReturnedTariff;
            if (unpaidRequestedTariffIDs.has(existingTariff.id)) {
              // The payor replaced this requested tariff with an assessed
              // tariff. Unclaimable closes it without losing what was billed.
              return {
                ...existingTariff,
                status: CLAIM_TARIFF_STATUS.UNCLAIMABLE,
              };
            }
            return existingTariff;
          });
        const updatedExistingTariffIDs = new Set(
          updatedExistingTariffs.map((tariff) => tariff.id)
        );
        const newTariffs = [
          ...updatedExistingTariffs,
          ...paidReturnedTariffs.filter(
            (tariff) => !updatedExistingTariffIDs.has(tariff.id)
          ),
        ];
        // A returned tariff contributes to paid, but not billed. Limit the
        // zero requested amount to debits explicitly accepted in this match.
        const updatedDebits = debits.map((debit) =>
          acceptedReturnedDebitIDs.has(debit.id)
            ? { ...debit, requestedAmount: '0.00' }
            : debit
        );
        const updatedClaim: Claim = {
          ...(selectedClaim as Claim),
          tariffs: newTariffs,
          debits: updatedDebits,
          status: CLAIM_STATUS.PAID,
        };
        await updateClaim.mutateAsync({
          dirtyFields: { tariffs: true, debits: true, status: true },
          newData: { ...updatedClaim },
        });
      } catch (err) {
        setLoading(false);
      } finally {
        setLoading(false);
        setOpen(false);
      }
    };

    const handleClose = () => {
      setShowQuery(false);
      setOpen(false);
    };

    const isAcceptDisabled =
      selectedTariffs.length === 0 || loading || returnedTariffs.length === 0;

    const hasOptions =
      unpaidRequestedTariffs.length > 0 || unmatchedDebits.length > 0;

    return (
      <Modal
        opened={open}
        onClose={handleClose}
        closeOnEscape
        withCloseButton
        size={showQuery ? 'xl' : 'xxl'}
        centered
        radius="lg"
        shadow="xl"
        overlayProps={{ blur: 3, opacity: 0.5 }}
        title={
          <Text fw={700} size="lg">
            {title}
          </Text>
        }
      >
        {loading ? (
          <Loader size="xl" type="dots" w="100%" />
        ) : (
          <>
            {!showQuery && (
              <>
                <MatchClaimModalAlert matchedTariffs={unmatchedDebits} />
                <MatchedTariffCard tariffs={matchedTariffs} debits={debits} />
                {hasOptions && (
                  <UnpaidUnmatchedCard
                    unpaidRequestedTariffs={unpaidRequestedTariffs}
                    unmatchedDebits={unmatchedDebits}
                    selectedTariffIDs={selectedTariffIDs}
                    setSelectedTariffIDs={setSelectedTariffIDs}
                    selectedTariffs={selectedTariffs}
                    loading={loading}
                    onQueryTariffs={handleQueryTariffs}
                    onAcceptTariffs={handleAcceptExtraTariffs}
                    isAcceptDisabled={isAcceptDisabled}
                    canCreateQuery={canCreateQuery}
                  />
                )}
              </>
            )}
            {showQuery && (
              <Grid justify="center" align="center" p={'md'}>
                <ClaimQueryModalContent
                  selectedClaim={modifiedClaim as Claim}
                  onClose={() => {
                    handleClose();
                  }}
                  onStepBackwards={() => setShowQuery(false)}
                />
              </Grid>
            )}
          </>
        )}
      </Modal>
    );
  }
);
